import React, { useEffect, useState } from "react";
import { cn } from "@/utils/utils";

type FlagComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;
type FlagSet = Record<string, FlagComponent | undefined>;

// The full flag set is ~50KB gzipped, so it loads on first use instead of
// shipping in the main bundle; a failed load is retried on the next mount, and
// every flag still waiting is updated when any load succeeds.
let loadedFlags: FlagSet | undefined;
let flagsRequest: Promise<FlagSet> | undefined;
const waitingForFlags = new Set<(flags: FlagSet) => void>();

const loadFlags = (): Promise<FlagSet> => {
  flagsRequest ??= import("country-flag-icons/react/3x2")
    .then((flags) => {
      const loaded = flags as unknown as FlagSet;
      loadedFlags = loaded;
      waitingForFlags.forEach((notify) => notify(loaded));
      return loaded;
    })
    .catch((error) => {
      flagsRequest = undefined;
      throw error;
    });
  return flagsRequest;
};

interface CountryFlagProps {
  // ISO 3166-1 alpha-2 code, e.g. "GB".
  code: string;
  className?: string;
}

// Decorative: the country is always named by the surrounding text or label.
export const CountryFlag: React.FC<CountryFlagProps> = ({
  code,
  className
}) => {
  const [flags, setFlags] = useState<FlagSet | undefined>(loadedFlags);

  useEffect(() => {
    if (flags) return;
    // Loaded between this render and the effect: nothing will notify us.
    if (loadedFlags) {
      setFlags(loadedFlags);
      return;
    }
    // Unsubscribing on unmount is what keeps an unmounted flag from updating.
    waitingForFlags.add(setFlags);
    loadFlags().catch(() => undefined);
    return () => {
      waitingForFlags.delete(setFlags);
    };
  }, [flags]);

  const Flag = flags?.[code];

  return (
    <span
      aria-hidden="true"
      data-test={`country-flag-${code}`}
      className={cn(
        "inline-flex h-[14px] w-[21px] shrink-0 overflow-hidden rounded-sm",
        !Flag && "bg-surface-elevation3",
        className
      )}
    >
      {Flag && <Flag className="size-full" />}
    </span>
  );
};
